import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orders as ordersApi, auth, creditApplications } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CreditCard, Package, TrendingUp, Eye, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  usePageTitle('Dashboard');
  const { user, updateUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [creditApp, setCreditApp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, profileRes, creditAppRes] = await Promise.all([
        ordersApi.getAll({ limit: 5 }),
        auth.getProfile(),
        creditApplications.getMine().catch(() => ({ data: { application: null } })),
      ]);
      setOrders(ordersRes.data.orders);
      updateUser(profileRes.data);
      setCreditApp(creditAppRes.data.application);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const creditUsed = user?.credit_used || 0;
  const creditLimit = user?.credit_limit || 5000;
  const creditAvailable = creditLimit - creditUsed;
  const creditPercentage = (creditUsed / creditLimit) * 100;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="container-wide py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-[#F1F5F9] w-1/4" />
          <div className="grid grid-cols-3 gap-6">
            <div className="h-32 bg-[#F1F5F9]" />
            <div className="h-32 bg-[#F1F5F9]" />
            <div className="h-32 bg-[#F1F5F9]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0F172A] mb-2">
          Welcome, {user?.contact_name}
        </h1>
        <p className="text-[#64748B]">{user?.company_name}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Credit Card */}
        <div className="border border-[#E2E8F0] p-6" data-testid="credit-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#002FA7]" />
            </div>
            <span className="text-sm font-medium text-[#64748B]">Credit Account</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#64748B]">Available</span>
              <span className="font-semibold text-green-600">${creditAvailable.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#64748B]">Used</span>
              <span className="text-[#0F172A]">${creditUsed.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#64748B]">Limit</span>
              <span className="text-[#0F172A]">${creditLimit.toFixed(2)}</span>
            </div>
            {user?.payment_terms_days && (
              <div className="flex justify-between text-sm">
                <span className="text-[#64748B]">Payment Terms</span>
                <span className="text-[#0F172A]">{user.payment_terms_days} days</span>
              </div>
            )}
            <div className="mt-3">
              <div className="h-2 bg-[#E2E8F0] overflow-hidden">
                <div
                  className="h-full bg-[#002FA7] transition-all duration-300"
                  style={{ width: `${Math.min(creditPercentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-[#64748B] mt-1">{creditPercentage.toFixed(1)}% used</p>
            </div>
            {creditUsed > 0 && (
              <div className="mt-4">
                <Link to="/payment">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    data-testid="pay-balance-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" /> Pay Balance (${creditUsed.toFixed(2)})
                  </Button>
                </Link>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
              {creditApp?.status === 'pending' ? (
                <p className="flex items-center justify-between text-sm text-amber-700" data-testid="credit-app-status">
                  <span>⏳ Credit application under review</span>
                  {creditApp.submitted_at && (
                    <span className="text-xs text-[#94A3B8]">
                      {new Date(creditApp.submitted_at).toLocaleDateString('en-AU')}
                    </span>
                  )}
                </p>
              ) : creditApp?.status === 'approved' ? (
                <p className="text-sm text-green-700" data-testid="credit-app-status">
                  ✅ Credit approved · Limit ${(creditApp.approved_limit || creditLimit).toFixed(0)}
                </p>
              ) : (
                <Link
                  to="/credit-application"
                  className="flex items-center justify-between text-sm text-[#002FA7] hover:underline"
                  data-testid="apply-credit-account-link"
                >
                  <span>
                    {creditApp?.status === 'rejected'
                      ? 'Reapply for credit account'
                      : creditLimit > 0
                        ? 'Apply for credit account'
                        : 'Apply for credit account'}
                  </span>
                  <span aria-hidden="true">→</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Orders Card */}
        <div className="border border-[#E2E8F0] p-6" data-testid="orders-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-[#002FA7]" />
            </div>
            <span className="text-sm font-medium text-[#64748B]">Total Orders</span>
          </div>
          <p className="text-3xl font-bold text-[#0F172A]">{orders.length}</p>
          <Link to="/orders" className="text-sm text-[#002FA7] hover:underline mt-2 inline-block">
            View all orders →
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="border border-[#E2E8F0] p-6" data-testid="actions-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#002FA7]" />
            </div>
            <span className="text-sm font-medium text-[#64748B]">Quick Actions</span>
          </div>
          <div className="space-y-2">
            <Link to="/products" className="block">
              <Button variant="outline" className="w-full justify-start">
                Browse Products
              </Button>
            </Link>
            <Link to="/cart" className="block">
              <Button variant="outline" className="w-full justify-start">
                View Cart
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="border border-[#E2E8F0]">
        <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center">
          <h2 className="text-lg font-semibold text-[#0F172A]">Recent Orders</h2>
          <Link to="/orders">
            <Button variant="ghost" size="sm">View All</Button>
          </Link>
        </div>
        
        {orders.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#64748B]">No orders yet</p>
            <Link to="/products">
              <Button className="mt-4 bg-[#002FA7] hover:bg-[#0025a0]">
                Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-[#E2E8F0]">
            {orders.slice(0, 5).map((order) => (
              <div key={order.id} className="p-4 hover:bg-[#F8FAFC] transition-colors">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-mono text-sm text-[#64748B]">{order.order_number}</p>
                    <p className="text-sm text-[#0F172A]">
                      {order.items.length} item(s) • ${order.total.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getStatusColor(order.status)}>
                      {order.status}
                    </Badge>
                    <Link to={`/orders/${order.id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
